package com.flowyride.feature.account.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.flowyride.core.domain.account.SessionState
import com.flowyride.feature.account.SessionViewModel
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.retry
import com.flowyride.feature.account.resources.startup_unreachable
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Nothing of the app is reachable without an approved account.
 * [content] gets the account button to place in its header; the button opens [settings], which gets the account's
 * section to show among its own, and how to close it.
 */
@Composable
fun AccountGate(
    viewModel: SessionViewModel = koinViewModel(),
    settings: @Composable (accountSection: @Composable () -> Unit, onBack: () -> Unit) -> Unit,
    content: @Composable (accountButton: @Composable () -> Unit) -> Unit,
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    when (val session = state.session) {
        SessionState.Unknown -> StartupScreen(state.unreachable, onRetry = viewModel::restore)
        SessionState.SignedOut -> AuthRoute()
        is SessionState.SignedIn -> {
            var showSettings by rememberSaveable { mutableStateOf(false) }
            var showAdmin by rememberSaveable { mutableStateOf(false) }
            var changingPassword by rememberSaveable { mutableStateOf(false) }
            Box(Modifier.fillMaxSize()) {
                // The planner stays composed under the settings and admin screens: the map and the ride are kept.
                content { AccountButton(user = session.user, onClick = { showSettings = true }) }
                if (showSettings) {
                    settings(
                        {
                            AccountSettingsSection(
                                user = session.user,
                                onOpenAdmin = { showAdmin = true },
                                onChangePassword = { changingPassword = true },
                                onSignOut = viewModel::signOut,
                            )
                        },
                        { showSettings = false },
                    )
                }
                if (showAdmin && session.user.isAdmin) {
                    AdminRoute(onBack = { showAdmin = false })
                }
                if (changingPassword) {
                    ChangePasswordDialog(onDismiss = { changingPassword = false })
                }
            }
        }
    }
}

@Composable
private fun StartupScreen(unreachable: Boolean, onRetry: () -> Unit) {
    Surface(Modifier.fillMaxSize()) {
        Box(contentAlignment = Alignment.Center) {
            if (unreachable) {
                Column(
                    Modifier.padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Text(
                        stringResource(Res.string.startup_unreachable),
                        style = MaterialTheme.typography.bodyLarge,
                        textAlign = TextAlign.Center,
                    )
                    Button(onClick = onRetry) { Text(stringResource(Res.string.retry)) }
                }
            } else {
                CircularProgressIndicator()
            }
        }
    }
}
